import { notifications } from "@mantine/notifications";
import { useNavigate } from "@tanstack/react-router";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useCallback, useEffect, useRef } from "react";
import { FORBIDDEN_KEY } from "../utils/fetch";
import { useUser } from "./useUser";

/**
 * Custom hook to synchronize user's preferred roles with their actual assigned roles
 * Ensures that any roles in the user's preferences that aren't valid user roles are filtered out
 * Redirects user to preferences page if new roles are detected
 */

export const useSyncUserRoles = () => {
    const { data: user, isLoading: userLoading } = useUser();
    const [isForbidden, setIsForbidden] = useLocalStorage(FORBIDDEN_KEY, false);
    const navigate = useNavigate();
    const initialized = useRef(false);
    const notificationShownRef = useRef(false);

    const [chatPreferences, setChatPreferences] = useLocalStorage<{
        selectedProductLines: string[];
        selectedDatasources: string[];
    }>("chatPreferences", {
        selectedProductLines: [],
        selectedDatasources: [],
    });

    const [availableDataSources, setAvailableDataSources] = useLocalStorage<string[]>(
        "availableDataSources",
        [],
    );

    const [notifiedRoles, setNotifiedRoles] = useLocalStorage<{
        timestamp: number;
        roleIds: string[];
    }>("notifiedNewRoles", { timestamp: 0, roleIds: [] });

    const [_, setStoredUserDatasources] = useLocalStorage<string[]>("userDatasources", []);

    const synchronizeUserRoles = useCallback(() => {
        if (user && isForbidden) {
            setIsForbidden(false);
        }

        if (initialized.current || userLoading || !user?.datasources) {
            return;
        }

        const userDatasources = user.datasources.filter((ds) => ds.has_access).map((ds) => ds.id);
        const isExternalUser = user.info?.roles?.includes("external") ?? false;

        setStoredUserDatasources(userDatasources);
        const isFreshState = availableDataSources.length === 0;

        if (!isFreshState && !isExternalUser) {
            const newRoles = userDatasources.filter((id) => !availableDataSources.includes(id));

            if (newRoles.length > 0) {
                if (!notificationShownRef.current) {
                    notifications.show({
                        title: "New roles detected",
                        message: "Please update your role preferences",
                        color: "orange",
                    });

                    setNotifiedRoles({
                        timestamp: Date.now(),
                        roleIds: [...notifiedRoles.roleIds, ...newRoles],
                    });

                    notificationShownRef.current = true;
                }
                const currentLocation = window.location.pathname;
                if (!currentLocation.includes("questionnaire")) {
                    navigate({ to: "/questionnaire" });
                }
            }
        }

        setAvailableDataSources(userDatasources);
        const filteredDatasources = chatPreferences.selectedDatasources.filter((dsId) =>
            userDatasources.includes(dsId),
        );

        if (
            JSON.stringify(filteredDatasources) !==
            JSON.stringify(chatPreferences.selectedDatasources)
        ) {
            setChatPreferences({
                ...chatPreferences,
                selectedDatasources: filteredDatasources,
            });
        }

        initialized.current = true;
    }, [
        user,
        userLoading,
        availableDataSources,
        setAvailableDataSources,
        chatPreferences,
        setChatPreferences,
        notifiedRoles,
        setNotifiedRoles,
        navigate,
        setStoredUserDatasources,
        isForbidden,
        setIsForbidden,
    ]);

    useEffect(() => {
        synchronizeUserRoles();
    }, [synchronizeUserRoles]);

    return {
        isForbidden,
    };
};
